import { Link } from 'react-router-dom';
import { rotuloDoExpediente } from '@/produtividade/expediente';

/**
 * ★★ WO lote 3b — O PLACAR DO DIA, no topo de Próximos Contatos.
 *
 * ★ Só DESENHA: toda conta vem de `placarDoDia` (puro, 36 casos).
 *
 * ★★ A hierarquia do desenho aprovado (`docs/wo-desenho-aprovado.html`, seção 1) é
 * deliberada: **número grande = ESFORÇO**, porque é o que a pessoa controla hoje; o
 * resultado vem na linha de baixo. ⚠ Pôr "fechamentos" em corpo 28 cobraria o que ela não
 * controla sozinha.
 *
 * ⚠ SEM META: cartão mostra as contagens e manda configurar — **sem barra**. Barra sem alvo
 * seria um desenho bonito medindo nada.
 */

const COR = { verde: 'verde', amarelo: 'amarelo', vermelho: 'vermelho' };
const horaDe = (agora) => `${String(agora.getHours()).padStart(2, '0')}h`;
const pct = (v) => (v === null || v === undefined ? '–' : `${Math.round(v)}%`);

/**
 * ★★★ A-2 — O EXPEDIENTE NÃO VEM MAIS POR PROP: ele vem **dentro de cada cartão**.
 *
 * ⚠⚠ Antes era `PlacarDoDia({ cartoes, agora, expediente })` — **um** expediente para a lista
 * inteira. ★ Com horário por pessoa, o rótulo da Talita (09:00–18:00) e o da Jéssica
 * (08:00–17:00) são diferentes, e ⚠ um componente que recebe um valor só **não tem como saber**
 * de quem ele é. ★★ Quem resolve é `cartaoDoDia`, que já sabe de quem é o cartão.
 */
export default function PlacarDoDia({ cartoes = [], agora = new Date(), isGestor = false }) {
  if (cartoes.length === 0) return null;
  return (
    <div className="placar-dia">
      {cartoes.map((c) => <Cartao key={c.id} c={c} agora={agora} isGestor={isGestor} />)}
    </div>
  );
}

/**
 * ★★★ A-7a — O LINK "DEFINA EM METAS E VENDAS" MUDOU, e muda por PAPEL.
 *
 * ⚠ `/metas` passou a redirecionar para `/dashboard`: um link para lá levaria a pessoa a uma
 * tela que não tem o campo que o texto promete. ★ Para o GESTOR o destino é `/indicadores`,
 * que é onde a meta por dia de cada pessoa passou a ser digitada ("Metas individuais").
 * ⚠⚠ Para a SDR **não há link nenhum**: ela não pode definir a própria meta, e um link que a
 * leva a uma tela onde ela não acha o campo é pior que a frase "peça ao gestor".
 */
function Cartao({ c, agora, isGestor = false }) {
  const { esforco, ritmo, resultado, pendencias } = c;
  const classe = ['pc', ritmo.semaforo ? COR[ritmo.semaforo] : ''].filter(Boolean).join(' ');
  // ⚠ `null` e 0 descem diferentes: null é domingo (sem ritmo), 0 é "ainda não abriu".
  const temRitmo = ritmo.fracao !== null && !c.semMeta;

  return (
    <div className={classe}>
      <div className="pc-nome">
        {c.nome}
        <small>{c.papel} · hoje {horaDe(agora)}</small>
      </div>

      <div className="pc-grande">
        {esforco.hoje}{' '}
        <span>
          {c.semMeta || c.feriadoHoje ? `${esforco.unidade} hoje` : `de ${esforco.meta} ${esforco.unidade} hoje`}
        </span>
      </div>

      <div className="pc-linha">
        Semana: <strong>{esforco.semana}</strong>
        {!c.semMeta && <> de {esforco.metaSemana}</>} {esforco.unidade}
        {' · '}
        {resultado.tipo === 'sdr' ? (
          <>Agendamentos: <strong>{resultado.hoje}</strong> hoje, {resultado.semana} na semana</>
        ) : (
          <>
            Fechamentos: <strong>{resultado.hoje}</strong> hoje, {resultado.semana} na semana
            {/* ⚠ conversão `null` vira "–", nunca 0%: sem comparecimento não há taxa. */}
            {' '}(conversão da semana {pct(resultado.conversaoSemana)})
          </>
        )}
      </div>

      {temRitmo ? (
        <div className="pc-ritmo">
          <div className="pc-ritmo-rotulo">
            {/* ★ O rótulo do expediente SAI DO CONFIG — nunca escrito à mão. */}
            {/* ★★ O rótulo sai do expediente DO CARTÃO — e ele já traz o almoço quando há. */}
            <span>Ritmo do dia (expediente {rotuloDoExpediente(c.dia, c.expediente)})</span>
            <span>{ritmo.atraso > 0 ? `${ritmo.atraso} atrás do ritmo` : 'no ritmo'}</span>
          </div>
          <div className={['pc-barra', ritmo.semaforo].filter(Boolean).join(' ')}>
            <i style={{ width: `${Math.min(100, esforco.meta > 0 ? (esforco.hoje / esforco.meta) * 100 : 0)}%` }} />
            {/* ★ a marca "agora": a fração do expediente já decorrida */}
            <b style={{ left: `${(ritmo.fracao ?? 0) * 100}%` }} />
          </div>
        </div>
      ) : (
        <p className="pc-sem-meta">
          {/* ★ 02/11/2026 — feriado marcado na agenda: sem meta e sem semáforo; o feito conta */}
          {c.feriadoHoje
            ? 'Feriado — sem meta hoje.'
            : ritmo.fracao === null
            ? 'Domingo não tem meta.'
            : isGestor
              ? <>Sem meta por dia. Defina em <Link to="/indicadores">Indicadores de Performance</Link>, em &ldquo;Metas individuais&rdquo;.</>
              : 'Sem meta por dia — peça ao gestor para definir a sua.'}
        </p>
      )}

      <div className="pc-pend">
        <Pendencia n={pendencias.semProximoContato.n} para={pendencias.semProximoContato.para}
          texto={(n) => `${n} lead${n === 1 ? '' : 's'} sem próximo contato`} />
        <Pendencia n={pendencias.semBaixa.n} para={pendencias.semBaixa.para}
          texto={(n) => `${n} agendamento${n === 1 ? '' : 's'} sem baixa`} />
        {pendencias.semFollowUp && (
          <Pendencia n={pendencias.semFollowUp.n} para={pendencias.semFollowUp.para}
            texto={(n) => `${n} lead${n === 1 ? '' : 's'} que não fecharam sem follow-up há ${pendencias.semFollowUp.dias}+ dias`} />
        )}
      </div>
    </div>
  );
}

/** ★ Zero não vira link: não há o que abrir, e um "0 pendências" azul convida ao clique vazio. */
function Pendencia({ n, para, texto }) {
  if (!n) return null;
  return <Link to={para}>{texto(n)}</Link>;
}
